/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Create models using Looker Looks", description = "Connect Hightouch to your Looker instance and build Hightouch models using existing Looks.", lastUpdatedDate = new Date(1714953600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Create models using Looker Looks",
        description: "Connect Hightouch to your Looker instance and build Hightouch models using existing Looks.",
        lastUpdatedDate: "Mon May 06 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Supported sources",
          "depth": 0,
          "id": "supported-sources"
        }, {
          "value": "Connect to Looker",
          "depth": 0,
          "id": "connect-to-looker"
        }, {
          "value": "Select a Look",
          "depth": 0,
          "id": "select-a-look"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Permissions",
          "depth": 1,
          "id": "permissions"
        }, {
          "value": "Looker URL",
          "depth": 1,
          "id": "looker-url"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      em: "em",
      h2: "h2",
      ul: "ul",
      li: "li",
      strong: "strong",
      code: "code",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["If you've already invested in modeling business data in ", _jsx(_components.a, {
          href: "https://www.looker.com/",
          children: "Looker"
        }), ", you may want to use your Looks as the basis for ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "Hightouch models"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "When you import a model from Looker, Hightouch converts your Look into a SQL query and runs it directly against your data warehouse. As you publish changes in Looker, your Hightouch model updates to reflect the latest version of your Look."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This extension uses Looker as a ", _jsx(_components.em, {
            children: "modeling method"
          }), ", not a ", _jsx(_components.em, {
            children: "data source"
          }), ". In other words, Looker provides the SQL query, not the query results. Before enabling this extension, you must first ", _jsx(_components.a, {
            href: "/getting-started/create-your-first-sync#connect-a-source",
            children: "create a source"
          }), " in Hightouch that connects to the same data warehouse used by Looker."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "supported-sources",
        children: "Supported sources"
      }), "\n", _jsx(_components.p, {
        children: "You can create models based on Looker Looks for the following sources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/amazon-athena",
            children: "Amazon Athena"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/greenplum",
            children: "Greenplum Database"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-looker",
        children: "Connect to Looker"
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/extensions",
          children: [_jsx(_components.strong, {
            children: "Extensions"
          }), " page"]
        }), " and select ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/looker/configuration",
          children: _jsx(_components.strong, {
            children: "Looker"
          })
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Tunnel"
          }), " - If you've previously set up an ", _jsx(_components.a, {
            href: "/security/ssh-tunneling",
            children: "SSH tunnel"
          }), " and want to use it to connect to Looker, you can select it here."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "URL"
          }), " - Hightouch uses this URL to connect to your Looker instance. The format is often ", _jsx(_components.code, {
            children: "https://<instance_name>.looker.com"
          }), ". Refer to ", _jsx(_components.a, {
            href: "https://docs.looker.com/reference/api-and-integration/api-getting-started#looker_api_path_and_port",
            children: "Looker's documentation"
          }), " if you have a non-standard Looker setup."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client ID"
          }), " - You can find this in your ", _jsx(_components.a, {
            href: "https://docs.looker.com/reference/api-and-integration/api-auth",
            children: "Looker API 4 credentials"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client secret"
          }), " - You can find this in your ", _jsx(_components.a, {
            href: "https://docs.looker.com/reference/api-and-integration/api-auth",
            children: "Looker API 4 credentials"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you need to create credentials, refer to Looker's documentation on ", _jsx(_components.a, {
          href: "https://docs.looker.com/reference/api-and-integration/api-auth#authentication_with_an_sdk",
          children: "authentication with an SDK"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "select-a-look",
        children: "Select a Look"
      }), "\n", _jsxs(_components.p, {
        children: ["After configuring your Looker credentials, you can create a model using a ", _jsx(_components.a, {
          href: "#supported-sources",
          children: "source that supports Looker"
        }), ". Select your source and then choose ", _jsx(_components.strong, {
          children: "Import Model from Looker"
        }), " as your modeling method."]
      }), "\n", _jsx(_components.p, {
        children: "On the next screen, you can select which Look you want to base your model on."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/looker-models-selector.png",
          alt: "Looker model selection in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.p, {
        children: "If you encounter any error messages during Look-based model setup, confirm the following about your credentials:"
      }), "\n", _jsx(_components.h3, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["You must be a Looker admin to retrieve your Looker connection URL. Check ", _jsx(_components.a, {
          href: "https://docs.looker.com/admin-options/settings/roles",
          children: "Looker's documentation on roles, permissions, and access"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "looker-url",
        children: "Looker URL"
      }), "\n", _jsxs(_components.p, {
        children: ["Check you're using the correct URL by consulting ", _jsx(_components.a, {
          href: "https://docs.looker.com/reference/api-and-integration/api-troubleshooting#endpoint_unreachable",
          children: "Looker's API connection troubleshooting guide"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
